import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CustomMaterialModule } from 'src/app/customModules/app-material-module';
import { KeystoreApiService } from 'src/app/construct-x/keystore/services/keystore-api.service';
import { SecretMetadata } from 'src/app/construct-x/keystore/models/keystore-api.models';
import { ConnectorAssignment } from './organisation-connector.service';

@Component({
  selector: 'app-connector-link-dialog', standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomMaterialModule],
  template: `
<h2 mat-dialog-title>{{ data.mode === 'external' ? 'Eigenen Connector verbinden' : 'Instanz mit Organisation verknüpfen' }}</h2>
<mat-dialog-content><div [formGroup]="form" class="link-form">
@if(data.mode !== 'external') {
  <mat-form-field appearance="outline"><mat-label>Organisation</mat-label><mat-select formControlName="organisationId">@for(org of data.organisations; track org.organisationId){<mat-option [value]="org.organisationId">{{ org.organisationName }}</mat-option>}</mat-select><mat-error>Bitte eine Organisation auswählen.</mat-error></mat-form-field>
}
<mat-form-field appearance="outline"><mat-label>Connector-URL</mat-label><input matInput formControlName="connectorUrl" placeholder="https://connector.example.org"><mat-error>Bitte eine gültige HTTP(S)-URL eingeben.</mat-error></mat-form-field>
@if(data.mode !== 'external') {
  <mat-form-field appearance="outline"><mat-label>Wallet-URL</mat-label><input matInput formControlName="walletUrl" placeholder="https://wallet.example.org"></mat-form-field>
  <mat-form-field appearance="outline"><mat-label>Wallet-Teilnehmer-ID</mat-label><input matInput formControlName="participantId"></mat-form-field>
  <mat-form-field appearance="outline"><mat-label>Wallet-API-Key im Organisations-Keystore</mat-label><mat-select formControlName="apiKeySecretId" [disabled]="secretsLoading || !form.controls.organisationId.value">@for(secret of secrets; track secret.id){<mat-option [value]="secret.id">{{ secret.label }}@if(secret.description){ · {{ secret.description }} }</mat-option>}</mat-select><mat-hint>Wählen Sie ein vorhandenes Secret; der Wert selbst wird nicht angezeigt.</mat-hint></mat-form-field>
  @if(secretsLoading){<p class="hint">Secrets werden geladen …</p>}
  @if(!secretsLoading && form.controls.organisationId.value && !secrets.length){<div class="secret-empty"><p>Für diese Organisation ist noch kein Secret verfügbar.</p><button mat-stroked-button color="primary" type="button" (click)="openKeystore()"><mat-icon>add</mat-icon>Neues Secret erstellen</button></div>}
  @if(secretError){<p class="error" role="alert">{{ secretError }} <button mat-button type="button" (click)="loadSecrets()">Erneut laden</button></p>}
} @else {<p>Die Plattformadministration prüft Ihre Verbindung und ordnet den Connector Ihrer Organisation zu.</p>}
</div></mat-dialog-content><mat-dialog-actions align="end"><button mat-button mat-dialog-close>Abbrechen</button><button mat-flat-button color="primary" type="button" (click)="save()" [disabled]="form.invalid">{{data.mode==='external'?'Verbindung anfragen':'Verknüpfen'}}</button></mat-dialog-actions>`,
  styles: [`.link-form{display:flex;flex-direction:column;gap:8px;padding-top:12px}mat-form-field{width:100%}.hint{margin:0;color:var(--app-muted)}.secret-empty{display:flex;align-items:center;justify-content:space-between;gap:12px}.secret-empty p,.error{margin:0}.error{color:var(--app-warn)}`]
})
export class ConnectorLinkDialogComponent implements OnInit {
  form = this.fb.group({
    organisationId: [this.data.assignment?.organisationId || null, Validators.required],
    connectorUrl: [this.data.assignment?.connectorUrl || '', [Validators.required, Validators.pattern(/^https?:\/\/[^\s]+$/)]],
    walletUrl: [this.data.assignment?.walletUrl || '', [Validators.required, Validators.pattern(/^https?:\/\/[^\s]+$/)]],
    participantId: [this.data.assignment?.participantId || '', Validators.required],
    apiKeySecretId: [this.data.assignment?.apiKeySecretId || '', Validators.required]
  });
  secrets: SecretMetadata[] = [];
  secretsLoading = false;
  secretError = '';

  constructor(private readonly fb: FormBuilder, private readonly ref: MatDialogRef<ConnectorLinkDialogComponent>,
    private readonly keystore: KeystoreApiService, private readonly router: Router,
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'external' | 'link'; assignment?: ConnectorAssignment; organisations?: { organisationId: number; organisationName: string }[] }) {
    if (data.mode === 'external') for (const key of ['organisationId', 'walletUrl', 'participantId', 'apiKeySecretId']) {
      this.form.get(key)!.clearValidators(); this.form.get(key)!.updateValueAndValidity();
    }
  }

  ngOnInit(): void {
    if (this.data.mode !== 'external') {
      this.form.controls.organisationId.valueChanges.subscribe(() => this.loadSecrets());
      this.loadSecrets();
    }
  }

  loadSecrets(): void {
    const organisationId = this.form.controls.organisationId.value;
    this.secrets = []; this.secretError = ''; this.form.controls.apiKeySecretId.setValue('');
    if (!organisationId) return;
    this.secretsLoading = true;
    this.keystore.listSecrets(organisationId).subscribe({
      next: response => {
        this.secrets = response.items;
        const selected = this.data.assignment?.organisationId === organisationId ? this.data.assignment?.apiKeySecretId : null;
        if (selected && this.secrets.some(secret => secret.id === selected)) this.form.controls.apiKeySecretId.setValue(selected);
        this.secretsLoading = false;
      },
      error: () => { this.secretError = 'Die Secrets dieser Organisation konnten nicht geladen werden. Richten Sie bei Bedarf zuerst den Vault ein.'; this.secretsLoading = false; }
    });
  }

  openKeystore(): void {
    const organisationId = this.form.controls.organisationId.value;
    if (!organisationId) return;
    this.ref.close(); void this.router.navigate(['/organisations', organisationId, 'keystore']);
  }

  save(): void { this.form.markAllAsTouched(); if (this.form.valid) this.ref.close(this.form.getRawValue()); }
}
